import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { PermissionChip, VerifiedBadge } from './index';
import { DESIGN_TOKENS } from '../../styles/theme';
import type { Pilot } from '../../types';
import { resolveAssetUrl } from '../../utils';
import { useI18n } from '../../i18n';

interface PilotCardProps {
  pilot: Pilot;
}

export function PilotCard({ pilot }: PilotCardProps) {
  const { t } = useI18n();
  const { company_information } = pilot;
  const { brand_graphics, company_data, contact_details, tax_information, verified, highlighted } = company_information || {};
  const personalData = pilot.personal_data ?? { name: '', surname: '' };
  const permissions = Array.isArray(pilot.permissions) ? pilot.permissions : [];

  const fullName = company_data?.company_name || `${personalData.name} ${personalData.surname}`.trim() || t('companies.pilotFallback');
  const initials = `${personalData.name[0] ?? ''}${personalData.surname[0] ?? ''}`.toUpperCase();

  const logoUrl = resolveAssetUrl(brand_graphics?.logo?.url);
  const coverUrl = resolveAssetUrl(brand_graphics?.coverImage?.url);

  return (
    <Card
      component={RouterLink}
      to={`/companies/pilots/${pilot.documentId}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        textDecoration: 'none',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: DESIGN_TOKENS.shadows.cardHover,
          textDecoration: 'none',
        },
      }}
    >
      {/* Cover image */}
      <Box
        sx={{
          height: 100,
          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
          backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Avatar / Logo */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -24,
            left: 20,
            width: 52,
            height: 52,
            borderRadius: '10px',
            border: `3px solid ${DESIGN_TOKENS.colors.surface}`,
            backgroundColor: DESIGN_TOKENS.colors.surface,
            overflow: 'hidden',
            boxShadow: DESIGN_TOKENS.shadows.card,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={`Logo ${fullName}`}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Avatar
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                color: DESIGN_TOKENS.colors.primary,
                fontWeight: 700,
                fontSize: '1.0625rem',
                borderRadius: 0,
              }}
            >
              {initials}
            </Avatar>
          )}
        </Box>
      </Box>

      <CardContent sx={{ pt: 4.5, pb: 2.5, px: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Name & badges */}
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.2 }}>
          {fullName}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <VerifiedBadge isVerified={verified ?? false} isHighlighted={highlighted ?? false} />
        </Box>

        {/* Permissions */}
        {permissions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {permissions.map((permission) => (
              <PermissionChip key={permission} permission={permission} />
            ))}
          </Box>
        )}

        {/* NIP */}
        {tax_information?.tax_identification_number && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            NIP: {tax_information.tax_identification_number}
          </Typography>
        )}

        {!verified && (
          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.error, fontWeight: 600, display: 'block', mb: 1 }}>{t('companies.unverifiedPilot')}</Typography>
        )}

        <Divider sx={{ my: 1.5, mt: 'auto' }} />

        {/* Contact details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          {contact_details?.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EmailIcon sx={{ fontSize: 13, color: DESIGN_TOKENS.colors.primary }} />
              <Typography variant="caption" color="text.secondary">
                {contact_details.email}
              </Typography>
            </Box>
          )}
          {contact_details?.phone_number && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PhoneIcon sx={{ fontSize: 13, color: DESIGN_TOKENS.colors.primary }} />
              <Typography variant="caption" color="text.secondary">
                {contact_details.phone_number}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: pilot.active ? DESIGN_TOKENS.colors.success : DESIGN_TOKENS.colors.textDisabled,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color={pilot.active ? 'success.main' : 'text.disabled'} fontWeight={600}>
            {pilot.active ? t('common.active') : t('common.inactive')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
